import { createContext, useContext, useState, ReactNode, useCallback } from 'react';

/**
 * Parlay Context
 * Manages the parlay builder state across bet pages
 */

export interface ParlayLeg {
  fightId: string;
  fightName: string; // e.g., "Fighter A vs Fighter B"
  selection: string; // Fighter name or prop outcome
  odds: number; // American odds
  type: 'moneyline' | 'prop'; // Type of bet
  marketName?: string; // For prop bets
}

interface ParlayContextType {
  isActive: boolean;
  legs: ParlayLeg[];
  startParlay: () => void;
  addLeg: (leg: ParlayLeg) => void;
  removeLeg: (index: number) => void;
  clearParlay: () => void;
  getTotalOdds: () => number;
  getDecimalOdds: () => number;
  hasFight: (fightId: string) => boolean;
}

const ParlayContext = createContext<ParlayContextType | undefined>(undefined);

export function ParlayProvider({ children }: { children: ReactNode }) {
  const [isActive, setIsActive] = useState(false);
  const [legs, setLegs] = useState<ParlayLeg[]>([]);

  const startParlay = useCallback(() => {
    setIsActive(true);
    setLegs([]);
  }, []);

  const addLeg = useCallback((leg: ParlayLeg) => {
    setLegs((prev) => {
      // Check if this fight is already in the parlay
      const existingIndex = prev.findIndex(l => l.fightId === leg.fightId);
      if (existingIndex >= 0) {
        // Don't allow multiple bets from the same fight
        throw new Error('Cannot add multiple bets from the same fight to a parlay');
      }
      // Add new leg
      return [...prev, leg];
    });
  }, []);

  const hasFight = useCallback((fightId: string): boolean => {
    return legs.some(leg => leg.fightId === fightId);
  }, [legs]);

  const removeLeg = useCallback((index: number) => {
    setLegs((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clearParlay = useCallback(() => {
    setIsActive(false);
    setLegs([]);
  }, []);

  /**
   * Convert American odds to decimal odds
   */
  const americanToDecimal = (americanOdds: number): number => {
    if (americanOdds > 0) {
      return americanOdds / 100 + 1;
    } else {
      return 100 / Math.abs(americanOdds) + 1;
    }
  };

  /**
   * Convert decimal odds to American odds
   */
  const decimalToAmerican = (decimalOdds: number): number => {
    if (decimalOdds >= 2.0) {
      return Math.round((decimalOdds - 1) * 100);
    } else {
      return Math.round(-100 / (decimalOdds - 1));
    }
  };

  /**
   * Get total decimal odds for the parlay
   */
  const getDecimalOdds = useCallback((): number => {
    if (legs.length === 0) return 1.0;
    
    return legs.reduce((total, leg) => {
      const decimalOdds = americanToDecimal(leg.odds);
      return total * decimalOdds;
    }, 1.0);
  }, [legs]);

  /**
   * Get total American odds for the parlay
   */
  const getTotalOdds = useCallback((): number => {
    const decimal = getDecimalOdds();
    return decimalToAmerican(decimal);
  }, [getDecimalOdds]);

  return (
    <ParlayContext.Provider
      value={{
        isActive,
        legs,
        startParlay,
        addLeg,
        removeLeg,
        clearParlay,
        getTotalOdds,
        getDecimalOdds,
        hasFight,
      }}
    >
      {children}
    </ParlayContext.Provider>
  );
}

export function useParlay() {
  const context = useContext(ParlayContext);
  if (!context) {
    throw new Error('useParlay must be used within ParlayProvider');
  }
  return context;
}

